export type McpClientId = 'claude' | 'chatgpt' | 'claude-code' | 'codex' | 'cursor' | 'other';

export type SnippetLanguage = 'bash' | 'json' | 'toml' | 'text';

export interface SnippetContext {
    serverUrl: string;
    token: string | null;
}

export interface Snippet {
    label: string;
    language: SnippetLanguage;
    code: string;
}

export interface ConnectionPath {
    /** Two to four short steps. They must not name vendor menus beyond one word. */
    steps: string[];
    snippet?: (ctx: SnippetContext) => Snippet;
    /** One caveat sentence. */
    note?: string;
}

export interface McpClient {
    id: McpClientId;
    /** Picker label. */
    name: string;
    /** Short description, for example "Web and desktop apps". */
    tagline: string;
    /** Path where the client signs in with OAuth. */
    signin?: ConnectionPath;
    /** Path where the client uses a personal access token. */
    token?: ConnectionPath;
    /** Placeholder for the name field of the create-token dialog. */
    tokenNameSuggestion: string;
    vendorDocsUrl?: string;
}

export const MCP_SERVER_PATH = '/api/mcp';

export const TOKEN_PLACEHOLDER = '<your token>';

/**
 * Builds the MCP server URL from the current origin.
 * The origin is passed in so the function stays testable.
 */
export function mcpServerUrl(origin: string): string {
    return `${origin}${MCP_SERVER_PATH}`;
}

/**
 * Returns the real token, or the placeholder when no token was created yet.
 */
export function tokenOrPlaceholder(token: string | null): string {
    return token ?? TOKEN_PLACEHOLDER;
}

// Default entry. It is also the first option in the picker.
// Verified end to end on 2026-09-06 (production, Claude.ai custom connector).
const CLAUDE_CLIENT: McpClient = {
    id: 'claude',
    name: 'Claude',
    tagline: 'Web and desktop apps',
    signin: {
        steps: [
            'Copy the server URL above.',
            'In Claude, open the Connectors settings and add a custom connector with this URL.',
            'Claude opens a Netfiscus sign-in page. Sign in and approve access.',
        ],
        note: 'No token needed. After you approve, Claude appears under Authorized apps.',
    },
    tokenNameSuggestion: 'Claude',
    vendorDocsUrl:
        'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
};

// Catalog order is the picker order.
export const MCP_CLIENTS: readonly McpClient[] = [
    CLAUDE_CLIENT,
    {
        // Not yet verified end to end. Documented: OAuth + PKCE + dynamic client registration, which the server supports.
        id: 'chatgpt',
        name: 'ChatGPT',
        tagline: 'Web app',
        signin: {
            steps: [
                'In ChatGPT, turn on developer mode in the settings.',
                'Add a new app or connector and paste the server URL as its connection.',
                'ChatGPT opens a Netfiscus sign-in page. Sign in and approve access.',
            ],
            note: 'ChatGPT signs in with your account and does not accept tokens. Developer mode is required.',
        },
        tokenNameSuggestion: 'ChatGPT',
        vendorDocsUrl: 'https://developers.openai.com/api/docs/guides/developer-mode',
    },
    {
        // Token path verified on 2026-09-06 (production). Sign-in path is documented (/mcp), not yet tried by us.
        id: 'claude-code',
        name: 'Claude Code',
        tagline: 'Terminal',
        signin: {
            steps: [
                'Run the command below in a terminal.',
                'Start Claude Code, run /mcp and sign in to Netfiscus in the browser that opens.',
            ],
            snippet: (ctx) => ({
                label: 'Add the server',
                language: 'bash',
                code: `claude mcp add --transport http netfiscus ${ctx.serverUrl}`,
            }),
        },
        token: {
            steps: [
                'Create a token below.',
                'Run the command below in a terminal. No sign-in is needed.',
            ],
            snippet: (ctx) => ({
                label: 'Add the server with a token',
                language: 'bash',
                code: `claude mcp add --transport http netfiscus ${ctx.serverUrl} --header "Authorization: Bearer ${tokenOrPlaceholder(ctx.token)}"`,
            }),
        },
        tokenNameSuggestion: 'Claude Code on my laptop',
        vendorDocsUrl: 'https://code.claude.com/docs/en/mcp',
    },
    {
        // Not yet verified end to end. Documented: codex mcp add --url, codex mcp login, --bearer-token-env-var.
        id: 'codex',
        name: 'Codex',
        tagline: 'OpenAI Codex CLI and IDE extension',
        signin: {
            steps: [
                'Run the commands below in a terminal.',
                'The second command opens a browser. Sign in to Netfiscus and approve access.',
            ],
            snippet: (ctx) => ({
                label: 'Add the server and sign in',
                language: 'bash',
                code: `codex mcp add netfiscus --url ${ctx.serverUrl}\ncodex mcp login netfiscus`,
            }),
            note: 'The Codex IDE extension shares this configuration.',
        },
        token: {
            steps: [
                'Create a token below.',
                'Put the token in an environment variable and add the server with the commands below.',
            ],
            snippet: (ctx) => ({
                label: 'Add the server with a token',
                language: 'bash',
                code: `export NETFISCUS_TOKEN="${tokenOrPlaceholder(ctx.token)}"\ncodex mcp add netfiscus --url ${ctx.serverUrl} --bearer-token-env-var NETFISCUS_TOKEN`,
            }),
        },
        tokenNameSuggestion: 'Codex on my laptop',
        vendorDocsUrl: 'https://developers.openai.com/codex/mcp',
    },
    {
        // Token config format verified against the vendor docs. Neither path tried by us.
        id: 'cursor',
        name: 'Cursor',
        tagline: 'Editor',
        signin: {
            steps: [
                'Add the server to mcp.json.',
                'Use ~/.cursor/mcp.json for every project, or .cursor/mcp.json in one project.',
                'When Cursor connects, sign in to Netfiscus and approve access.',
            ],
            snippet: (ctx) => ({
                label: 'mcp.json',
                language: 'json',
                code: [
                    '{',
                    '  "mcpServers": {',
                    '    "netfiscus": {',
                    `      "url": "${ctx.serverUrl}"`,
                    '    }',
                    '  }',
                    '}',
                ].join('\n'),
            }),
        },
        token: {
            steps: [
                'Create a token below.',
                'Add the server to mcp.json with the token in the Authorization header.',
            ],
            snippet: (ctx) => ({
                label: 'mcp.json with a token',
                language: 'json',
                code: [
                    '{',
                    '  "mcpServers": {',
                    '    "netfiscus": {',
                    `      "url": "${ctx.serverUrl}",`,
                    '      "headers": {',
                    `        "Authorization": "Bearer ${tokenOrPlaceholder(ctx.token)}"`,
                    '      }',
                    '    }',
                    '  }',
                    '}',
                ].join('\n'),
            }),
        },
        tokenNameSuggestion: 'Cursor on my laptop',
        vendorDocsUrl: 'https://cursor.com/docs/context/mcp',
    },
    {
        // Generic entry. No vendor docs link.
        id: 'other',
        name: 'Other',
        tagline: 'Any MCP client',
        signin: {
            steps: [
                "Look for MCP servers, connectors or integrations in the tool's settings.",
                'Add a remote server of type Streamable HTTP with the server URL.',
                'If the tool supports OAuth, it opens a Netfiscus sign-in page. Sign in and approve access.',
            ],
        },
        token: {
            steps: [
                'Create a token below.',
                'Send it in the Authorization header of every request, as shown below.',
            ],
            snippet: (ctx) => ({
                label: 'HTTP header',
                language: 'text',
                code: `Authorization: Bearer ${tokenOrPlaceholder(ctx.token)}`,
            }),
            note: 'Examples: VS Code, Gemini CLI, scripts.',
        },
        tokenNameSuggestion: 'My tool',
    },
];

/**
 * Returns the catalog entry for an id, or the default entry when the id is unknown.
 */
export function findMcpClient(id: McpClientId): McpClient {
    return MCP_CLIENTS.find((client) => client.id === id) ?? CLAUDE_CLIENT;
}
